import React, { useEffect, useMemo } from 'react'
import { InfoSheetPanel, DataBlock, Row, Separator, Button } from '@fs/zion-ui'
import { FormSelect, useZionFormContext, useWatch } from '@fs/zion-form'
import { useTranslation } from 'react-i18next'
import zionDebug from '@fs/zion-debug'
import { getElementTypeDisplayLabel, useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { getNextUuid } from '../../../../../shared/utils/utils'
import PanelHeader from '../shared/PanelHeader'
import ExpandableInputWithAddButton from './expandableInput/ExpandableInputWithAddButton'

const debug = zionDebug('rip:R2:AddPerson')

export default function AddName({ saving, hasSelectedHighlights, splitScreenArk, useSexField = false }) {
  const { addPersonGenericOverlay, editHighlightsOverlay, topMostPanel, closeOverlay } = useOverlayContext()
  const state = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const { formState, getValues, reset, setValue, control, trigger } = useZionFormContext()
  const [givenNameFieldId, surnameFieldId, sexFieldId] = getValues(['givenNameFieldId', 'surnameFieldId', 'sexFieldId'])
  const [givenCheckbox, surnameCheckbox] = useWatch({
    name: ['givenCheckbox', 'surnameCheckbox'],
    control,
  })

  const givenDisplayLabel = getElementTypeDisplayLabel(
    { elementType: 'FIELD', fieldType: 'NAME_GN' },
    state?.types,
    t('type-unknown.label', '<Unknown Type>')
  )
  const surnameDisplayLabel = getElementTypeDisplayLabel(
    { elementType: 'FIELD', fieldType: 'NAME_SURN' },
    state?.types,
    t('type-unknown.label', '<Unknown Type>')
  )
  const sexDisplayLabel = t('edit-essential-info.sex-code', 'Sex')
  const { isValid } = formState

  const nameTypeSelectOptions = useMemo(
    () =>
      state.types?.names?.map((name) => {
        return {
          value: name.nameType,
          label: name.displayLabel,
        }
      }),
    [state.types?.names]
  )

  // The back arrow on the panel was clicked
  const userClosedPanel = () => {
    closeOverlay('addPersonPage1Generic')
    h.clearEdits()
  }

  useEffect(() => {
    if (addPersonGenericOverlay.isOpen) {
      reset()
      setValue('givenNameFieldId', getNextUuid()) // The field id has to be created beforehand in order for highlights to be attached to it
      setValue('surnameFieldId', getNextUuid()) // The field id has to be created beforehand in order for highlights to be attached to it
      setValue('sexFieldId', getNextUuid())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      addPersonGenericOverlay?.isOpen &&
      topMostPanel() === 'addPersonPage1Generic' &&
      !editHighlightsOverlay.isOpen
    ) {
      h.setDisplayHighlights({
        mainPartitionKey: state.currentImageArk,
        splitScreenPartitionKey: splitScreenArk,
        customFilter: (hi) => hi.personIds.length > 0,
        fieldTypes: ['NAME_SURN', 'NAME_SUF', 'NAME_GN', 'SEX_CODE'],
        elementIds: [givenNameFieldId, surnameFieldId, sexFieldId],
      })
    }
    // trigger check the validation when the overlay close
    trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addPersonGenericOverlay?.isOpen,
    editHighlightsOverlay.isOpen,
    state.currentImageArk,
    state.images,
    topMostPanel,
    givenNameFieldId,
    surnameFieldId,
    sexFieldId,
  ])

  debug('Render: ', { state, isValid })

  return (
    <InfoSheetPanel
      {...addPersonGenericOverlay}
      close={userClosedPanel}
      headingColor="purple"
      title={t('add-person.panel-back', 'Back')}
      footer={
        <Row alignX="end" gutters="md">
          <Button
            linkName="Add Person Cancel"
            data-testid="panel-cancel-button"
            emphasis="low"
            disabled={saving}
            onClick={() => {
              h.clearEdits()
              closeOverlay('addPersonPage1Generic')
            }}
          >
            {t('edit-shared-surname-footer.cancel', 'Cancel')}
          </Button>
          <Button
            linkName="Add Person Next"
            data-testid="panel-forward-button"
            emphasis="high"
            disabled={saving || !isValid}
            form="add-person-form"
            type="submit"
          >
            {t('edit-shared-surname-footer.next', 'Next')}
          </Button>
        </Row>
      }
    >
      <PanelHeader
        heading={t('add-person.panel-heading', 'Add Name')}
        caption={t('add-person.caption', 'Select a name part to continue.')}
      />
      <Separator size="xs" />
      <DataBlock
        heading={t('add-person.subtitle', 'Select and fill out a name part to continue.')}
        data={t('add-person.body-text', 'Must have at least one name part and a highlight.')}
      />
      <Separator size="xs" />
      <FormSelect
        label={t('add-person.name-type-select', 'Name Type')}
        placeholder={t('add-person.name-type-select', 'Name Type')}
        name="nameType"
        options={nameTypeSelectOptions}
      />

      <ExpandableInputWithAddButton
        fieldId={givenNameFieldId}
        fieldType="NAME_GN"
        fieldName="givenField"
        checkboxValue={givenCheckbox}
        checkboxName="givenCheckbox"
        testId="given-name"
        displayLabel={givenDisplayLabel}
        addText={t('add-person.add-given-name', 'Add given name')}
        removeText={t('add-person.remove-given-name', 'Remove given name')}
        setValue={setValue}
        hasSelectedHighlights={hasSelectedHighlights}
      />

      <ExpandableInputWithAddButton
        fieldId={surnameFieldId}
        fieldType="NAME_SURN"
        fieldName="surnameField"
        checkboxValue={surnameCheckbox}
        checkboxName="surnameCheckbox"
        testId="surname"
        displayLabel={surnameDisplayLabel}
        addText={t('add-person.add-surname', 'Add surname')}
        removeText={t('add-person.remove-surname', 'Remove surname')}
        setValue={setValue}
        hasSelectedHighlights={hasSelectedHighlights}
      />

      {useSexField && (
        <ExpandableInputWithAddButton
          fieldId={sexFieldId}
          fieldType="SEX_CODE"
          fieldName="sexField"
          checkboxValue={false}
          testId="sex"
          displayLabel={sexDisplayLabel}
          addText={t('add-person.add-sex', 'Add Sex')}
          removeText={t('add-person.remove-sex', 'Remove Sex')}
          setValue={setValue}
          hasSelectedHighlights={hasSelectedHighlights}
        />
      )}
    </InfoSheetPanel>
  )
}
